import type { FC } from "react";
import toast from "react-hot-toast";
import { useForm, useFieldArray, Controller, Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { thunks } from "src/thunks/content-template";
import { useDispatch, useSelector } from "src/store";
import { RouterLink } from "src/components/router-link";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import { createRoute } from "react-router-url-params";
import LoadingButton from "@mui/lab/LoadingButton";
import React, { useState } from "react";
import { TextEditor } from "src/components/core/text-editor/text-editor";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
    instruction: z
      .string()
      .min(1, t(tokens.general.validators.required) as string),
    examples: z.array(
      z.object({
        value: z
          .string()
          .min(1, t(tokens.general.validators.required) as string),
      })
    ),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

export const ContentTemplateCreateForm: FC = (props) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { strategyId } = useSelector((state) => state.settings);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      instruction: "",
      examples: [],
    },
    resolver: zodResolver(validationSchema),
  });

  // Setup field array for examples
  const { fields, append, remove } = useFieldArray({
    control: control as Control<FormValues>,
    name: "examples",
  });

  const onSubmit = async (data: FormValues): Promise<void> => {
    setIsSubmitting(true);
    try {
      const contentTemplate = await dispatch(
        thunks.createContentTemplate(strategyId!, {
          name: data.name,
          instruction: data.instruction,
          examples: data.examples.map((example) => example.value),
        })
      );
      toast.success(t(tokens.contentTemplates.create.form.successMessage));
      router.push(
        createRoute(paths.contentTemplates.details).link({
          contentTemplateId: contentTemplate.id,
        })
      );
    } catch (err) {
      console.error(err);
      toast.error(t(tokens.general.formError));
      setError("root", { message: "Submission failed" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.name}
                    fullWidth
                    helperText={errors.name?.message}
                    label={t(tokens.contentTemplates.create.form.name)}
                  />
                )}
              />

              <Controller
                name="instruction"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.instruction}
                    fullWidth
                    multiline
                    minRows={5}
                    helperText={errors.instruction?.message}
                    label={t(tokens.contentTemplates.create.form.instruction)}
                  />
                )}
              />

              <Stack spacing={2}>
                <Typography
                  variant="h6"
                  sx={{ mb: 0 }}
                >
                  {t(tokens.contentTemplates.create.form.examples)}
                </Typography>

                {fields.length === 0 && (
                  <Typography variant="body2">
                    {t(tokens.contentTemplates.create.form.noExamples)}
                  </Typography>
                )}

                {fields.map((field, index) => (
                  <React.Fragment key={field.id}>
                    <Stack
                      spacing={3}
                      direction={{
                        xs: "column",
                        sm: "row",
                      }}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: "100%",
                        }}
                      >
                        <Controller
                          name={`examples.${index}.value`}
                          control={control as Control<FormValues>}
                          render={({ field }) => (
                            <TextEditor
                              showHeaderTool={true}
                              content={field.value}
                              onUpdate={(content) => {
                                field.onChange(content.editor.getHTML());
                              }}
                              placeholder={t(
                                tokens.general.placeholders.editor
                              )}
                            />
                          )}
                        />
                        {errors.examples?.[index] && (
                          <Typography
                            color="error"
                            variant="caption"
                          >
                            {errors.examples[index]?.message}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                    <Box>
                      <Link
                        color={"error"}
                        sx={{
                          cursor: "pointer",
                        }}
                        onClick={() => remove(index)}
                        variant="subtitle2"
                      >
                        {t(tokens.general.buttons.delete)}
                      </Link>
                    </Box>
                  </React.Fragment>
                ))}
                <Divider />
                <Box>
                  <Button
                    onClick={() => append({ value: "" })}
                    variant="outlined"
                    color="primary"
                  >
                    {t(tokens.general.buttons.add)}
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <Stack
          alignItems="center"
          direction="row-reverse"
          justifyContent="flex-end"
          spacing={1}
        >
          <Button
            href={paths.contentTemplates.list}
            color="inherit"
            component={RouterLink}
          >
            {t(tokens.general.buttons.cancel)}
          </Button>
          <LoadingButton
            type="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            variant="contained"
          >
            {t(tokens.general.buttons.create)}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};
